// The Graphy API client the UI depends on. Every call is bound to a project +
// snapshot; the client never trusts a client-supplied tenant id (the server
// derives authority from the session), mirroring the backend MCP/authz rules.

import type {
  AffectedSet,
  ChangesDelta,
  ChangesSnapshot,
  CommunitiesResult,
  CommunityOwnership,
  Direction,
  HubsResult,
  MapEdges,
  SystemMapData,
  McpInvokeRequest,
  McpResult,
  McpTool,
  NeighborhoodResult,
  NodePage,
  NodeRef,
  OverviewResult,
  PathResult,
  LocalReviewStatus,
  PRImpact,
  Provenance,
  PullRequestItem,
  ReviewThread,
  SnapshotRef,
  SourceExcerpt,
  WikiArticle,
  WikiIndexItem,
} from "./types";

export interface Scope {
  projectId: string;
  snapshotId: string;
}

export interface NeighborhoodQuery extends Scope {
  origin: string;
  direction: Direction;
  depth: number;
  provenance?: Provenance;
  nodeBudget?: number;
}

export interface PathQuery extends Scope {
  origin: string;
  target: string;
  undirected: boolean;
  provenance?: Provenance;
}

export interface SearchQuery extends Scope {
  query?: string;
  cursor?: string | null;
  pageSize?: number;
}

/** Thrown for a typed, safe API error (non-enumerating, like the backend). */
export class ApiError extends Error {
  constructor(
    public code: string,
    message: string,
  ) {
    super(message);
  }
}

export interface GraphyClient {
  overview(scope: { projectId: string }): Promise<OverviewResult>;
  snapshot(scope: { projectId: string }): Promise<SnapshotRef | null>;
  nodes(scope: Scope): Promise<NodeRef[]>;
  mapEdges(scope: Scope): Promise<MapEdges>;
  systemMap(scope: Scope): Promise<SystemMapData>;
  neighborhood(q: NeighborhoodQuery): Promise<NeighborhoodResult>;
  shortestPath(q: PathQuery): Promise<PathResult>;
  communities(scope: Scope): Promise<CommunitiesResult>;
  hubs(scope: Scope, top?: number): Promise<HubsResult>;
  searchNodes(q: SearchQuery): Promise<NodePage>;
  affectedSet(scope: Scope, changed: string[]): Promise<AffectedSet>;
  source(scope: Scope, symbolId: string, context?: number): Promise<SourceExcerpt>;
  mcpTools(scope: { projectId: string }): Promise<McpTool[]>;
  mcpInvoke(req: McpInvokeRequest, signal?: AbortSignal): Promise<McpResult>;
  changesSnapshot(scope: { projectId: string }): Promise<ChangesSnapshot>;
  changes(scope: { projectId: string }, cursor: number): Promise<ChangesDelta>;
  contributors(scope: { projectId: string }): Promise<CommunityOwnership[]>;
  correctOwnership(req: { projectId: string; communityId: number; owners: string[]; reason: string }): Promise<{ ok: boolean }>;
  wikiIndex(scope: { projectId: string }): Promise<WikiIndexItem[]>;
  wikiArticle(scope: { projectId: string }, articleId: string): Promise<WikiArticle>;
  wikiReview(req: { projectId: string; articleId: string }): Promise<WikiArticle>;
  wikiRegeneration(req: { projectId: string; articleId: string; action: "accept" | "discard" }): Promise<WikiArticle>;
  prs(scope: { projectId: string }): Promise<PullRequestItem[]>;
  prDetail(scope: { projectId: string }, prId: string): Promise<PullRequestItem>;
  prImpact(scope: { projectId: string }, prId: string): Promise<PRImpact>;
  prReview(scope: { projectId: string }, prId: string): Promise<ReviewThread>;
  prReviewNote(req: { projectId: string; prId: string; body: string }): Promise<ReviewThread>;
  prReviewStatus(req: { projectId: string; prId: string; status: LocalReviewStatus }): Promise<ReviewThread>;
  prReviewRequest(req: { projectId: string; prId: string; reviewer: string }): Promise<ReviewThread>;
}

/** HTTP implementation against the FastAPI backend (ADR-web-stack).
 *
 * The app is embedded behind the VisionHammer gateway, which authenticates each
 * request by a JWT Bearer token. The token is not ours to store: it is relayed
 * by the platform (see `auth/embeddedAuth`) and read lazily per request via
 * `getToken`, so a refreshed token is picked up without rebuilding the client.
 * On a 401 we signal `onUnauthorized` so the host can re-relay a fresh token. */
export class HttpGraphyClient implements GraphyClient {
  constructor(
    private baseUrl: string,
    private getToken?: () => string | null,
    private onUnauthorized?: () => void,
  ) {}

  private authHeaders(extra?: Record<string, string>): Record<string, string> {
    const headers: Record<string, string> = { ...extra };
    const token = this.getToken?.();
    if (token) headers["Authorization"] = `Bearer ${token}`;
    return headers;
  }

  // Resolve an endpoint path against the gateway base. The gateway BFF OWNS the
  // service's `/api` prefix (its contract forwards `{base-url}/api{path}`), so we
  // send the path WITHOUT `api/`; the gateway re-adds it. Sending `api/...` here
  // would reach the service as `/api/api/...` (404). VITE_API_BASE therefore ends
  // at `.../api/v1/graphy/` and the path is the logical resource (`graph/...`).
  private resolveUrl(path: string): URL {
    const rel = path.startsWith("api/") ? path.slice("api/".length) : path;
    return new URL(rel, this.baseUrl);
  }

  private async get<T>(path: string, params: object): Promise<T> {
    const url = this.resolveUrl(path);
    for (const [k, v] of Object.entries(params)) {
      if (v !== undefined && v !== null) url.searchParams.set(k, String(v));
    }
    let res: Response;
    try {
      res = await fetch(url.toString(), { credentials: "include", headers: this.authHeaders() });
    } catch {
      throw new ApiError("service_failure", "The service is temporarily unavailable.");
    }
    return this.parse<T>(res);
  }

  private async post<T>(path: string, body: object, signal?: AbortSignal): Promise<T> {
    const url = this.resolveUrl(path);
    let res: Response;
    try {
      res = await fetch(url.toString(), {
        method: "POST",
        credentials: "include",
        headers: this.authHeaders({ "Content-Type": "application/json" }),
        body: JSON.stringify(body),
        signal,
      });
    } catch (e) {
      if (e instanceof DOMException && e.name === "AbortError") throw e; // propagate cancel
      throw new ApiError("service_failure", "The service is temporarily unavailable.");
    }
    return this.parse<T>(res);
  }

  private async parse<T>(res: Response): Promise<T> {
    if (!res.ok) {
      if (res.status === 401) {
        // Session not yet relayed, or the relayed token expired: ask the host to
        // re-send the current token. The call still fails (honest state), and the
        // next call carries the refreshed token.
        this.onUnauthorized?.();
        throw new ApiError("unauthenticated", "Your session needs to reconnect.");
      }
      const code = res.status === 404 ? "not_found" : res.status === 403 ? "inaccessible" : "error";
      throw new ApiError(code, "The requested item is not available.");
    }
    return (await res.json()) as T;
  }

  overview(scope: { projectId: string }) {
    return this.get<OverviewResult>("api/overview", scope);
  }
  snapshot(scope: { projectId: string }) {
    return this.get<SnapshotRef | null>("api/graph/snapshot", scope);
  }
  nodes(scope: Scope) {
    return this.get<NodeRef[]>("api/graph/nodes", scope);
  }
  systemMap(scope: Scope) {
    return this.get<SystemMapData>("api/graph/system-map", scope);
  }
  mapEdges(scope: Scope) {
    return this.get<MapEdges>("api/graph/map", scope);
  }
  neighborhood(q: NeighborhoodQuery) {
    return this.get<NeighborhoodResult>("api/graph/neighbors", q);
  }
  shortestPath(q: PathQuery) {
    return this.get<PathResult>("api/graph/path", q);
  }
  communities(scope: Scope) {
    return this.get<CommunitiesResult>("api/graph/communities", scope);
  }
  hubs(scope: Scope, top = 10) {
    return this.get<HubsResult>("api/graph/hubs", { ...scope, top });
  }
  searchNodes(q: SearchQuery) {
    return this.get<NodePage>("api/graph/search", q);
  }
  affectedSet(scope: Scope, changed: string[]) {
    return this.get<AffectedSet>("api/graph/affected", { ...scope, changed: changed.join(",") });
  }
  source(scope: Scope, symbolId: string, context = 3) {
    return this.get<SourceExcerpt>("api/source", { ...scope, symbolId, context });
  }
  mcpTools(scope: { projectId: string }) {
    return this.get<McpTool[]>("api/mcp/tools", scope);
  }
  mcpInvoke(req: McpInvokeRequest, signal?: AbortSignal) {
    return this.post<McpResult>("api/mcp/invoke", req, signal);
  }
  changesSnapshot(scope: { projectId: string }) {
    return this.get<ChangesSnapshot>("api/changes/snapshot", scope);
  }
  changes(scope: { projectId: string }, cursor: number) {
    return this.get<ChangesDelta>("api/changes", { ...scope, cursor });
  }
  contributors(scope: { projectId: string }) {
    return this.get<CommunityOwnership[]>("api/contributors", scope);
  }
  correctOwnership(req: { projectId: string; communityId: number; owners: string[]; reason: string }) {
    return this.post<{ ok: boolean }>("api/contributors/correct", req);
  }
  wikiIndex(scope: { projectId: string }) {
    return this.get<WikiIndexItem[]>("api/wiki", scope);
  }
  wikiArticle(scope: { projectId: string }, articleId: string) {
    return this.get<WikiArticle>("api/wiki/article", { ...scope, articleId });
  }
  wikiReview(req: { projectId: string; articleId: string }) {
    return this.post<WikiArticle>("api/wiki/review", req);
  }
  wikiRegeneration(req: { projectId: string; articleId: string; action: "accept" | "discard" }) {
    return this.post<WikiArticle>("api/wiki/regeneration", req);
  }
  prs(scope: { projectId: string }) {
    return this.get<PullRequestItem[]>("api/prs", scope);
  }
  prDetail(scope: { projectId: string }, prId: string) {
    return this.get<PullRequestItem>("api/prs/detail", { ...scope, prId });
  }
  prImpact(scope: { projectId: string }, prId: string) {
    return this.get<PRImpact>("api/prs/impact", { ...scope, prId });
  }
  prReview(scope: { projectId: string }, prId: string) {
    return this.get<ReviewThread>("api/prs/review", { ...scope, prId });
  }
  prReviewNote(req: { projectId: string; prId: string; body: string }) {
    return this.post<ReviewThread>("api/prs/review/note", req);
  }
  prReviewStatus(req: { projectId: string; prId: string; status: LocalReviewStatus }) {
    return this.post<ReviewThread>("api/prs/review/status", req);
  }
  prReviewRequest(req: { projectId: string; prId: string; reviewer: string }) {
    return this.post<ReviewThread>("api/prs/review/request", req);
  }
}
